/**
 * klubb-header-hydrate — client-side hydration of the active-club indicator.
 *
 * Storefront Liquid can't read cookies, so the server only resolves the
 * active club from URL patterns (catalog collection pages). On every other
 * page (homepage, search, account, unrelated collections, etc.) the server
 * renders the unselected state by default. This module fills in the
 * selected-club affordances client-side, using window.klubb.activeClub
 * (which klubb-club-state.js already hydrates from the long-lived
 * klubb_active cookie at boot).
 *
 * Idempotent: if the server already rendered the chip / crest (URL match),
 * this is a no-op.
 *
 * Also decides which club a visit selects (adoption / switch offer): the club
 * in the path, a ?club=<slug> link (22.10.2026, for pages with no club in the
 * path), or a club-specific product. See docs/club-experience.md.
 */

(function () {
  'use strict';

  // Deferred classic scripts (this one) run before the module scripts further
  // down the page, so window.klubbCartReconcile is not there yet at boot.
  // DOMContentLoaded fires after every deferred/module script has run.
  var dclDone = document.readyState === 'complete';
  document.addEventListener('DOMContentLoaded', function () { dclDone = true; });
  function whenScriptsReady(fn) {
    if (dclDone) fn();
    else document.addEventListener('DOMContentLoaded', function () { fn(); }, { once: true });
  }

  // Local copy of klubb-shared.js escapeHtml — this classic script can't import modules.
  function escapeHtml(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, function (ch) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch];
    });
  }

  function getInitials(club) {
    if (club.crest_initials) return club.crest_initials;
    var src = club.short_name || club.name || '';
    return src.slice(0, 2).toUpperCase();
  }

  // Inner content of a .klubb-crest — the logo image when the club has one,
  // matching the server-side klubb-crest snippet, else initials. Expects the
  // caller to pass already-escaped name + initials.
  function crestInnerHtml(club, name, initials) {
    if (club.crest_image_url) {
      var ratio = Number(club.crest_image_ratio) || 0;
      var imgClass = ratio >= 0.75 && ratio <= 1.35 ? 'klubb-crest__img klubb-crest__img--cover' : 'klubb-crest__img';
      return '<img class="' + imgClass + '" src="' + escapeHtml(club.crest_image_url) +
        '" alt="' + name + '" loading="lazy">';
    }
    return '<span class="klubb-crest__initials">' + initials + '</span>';
  }

  // Local copy of klubb-shared.js clubCatalogHref — this classic script can't import modules.
  function getCatalogHref(club) {
    var handle = club.catalog_collection_handle || ((club.slug || '') + '-katalog');
    return '/collections/' + handle;
  }

  function chipHtml(club, yourClubLabel, clubHomeLabel) {
    var initials = escapeHtml(getInitials(club));
    var primary = escapeHtml(club.primary_color || '#0a0a0a');
    var secondary = escapeHtml(club.secondary_color || '#ffffff');
    var name = escapeHtml(club.name || '');
    var ariaLabel = escapeHtml(String(clubHomeLabel || '').replace('%CLUB%', club.name || ''));
    var crestSize = 36;
    var fontSize = Math.round(crestSize * 0.32);
    // A "home" link to the club's catalog, not a picker opener — picking a
    // club should feel permanent. Switching is done via the "Bytt klubb" link.
    return (
      '<a href="' + escapeHtml(getCatalogHref(club)) + '"' +
      ' class="klubb-nav-item klubb-nav-item--primary klubb-club-chip"' +      ' aria-label="' + ariaLabel + '">' +
      '<div class="klubb-crest" role="img" aria-label="' + name + '"' +
      ' style="width:' + crestSize + 'px;height:' + crestSize + 'px;background:' + primary +
      ';color:' + secondary + ';font-size:' + fontSize + 'px;">' +
      crestInnerHtml(club, name, initials) +
      '</div>' +
      '<span class="klubb-club-chip__labels">' +
      '<span class="klubb-club-chip__eyebrow">' + escapeHtml(yourClubLabel) + '</span>' +
      '<span class="klubb-club-chip__name">' + name + '</span>' +
      '</span>' +
      '</a>'
    );
  }

  function swapLinkHtml(swapLabel) {
    // The chip is now a home link, so the picker is reached via this
    // "Bytt klubb" link. The server only renders it on URL-resolved club
    // pages, so hydration must add it everywhere else the chip appears.
    return (
      '<button type="button"' +
      ' class="klubb-nav-item klubb-header__swap-club-link"' +      ' data-klubb-open-picker>' +
      escapeHtml(swapLabel) +
      '</button>'
    );
  }

  function crestIconBtnHtml(club) {
    var initials = escapeHtml(getInitials(club));
    var primary = escapeHtml(club.primary_color || '#0a0a0a');
    var secondary = escapeHtml(club.secondary_color || '#ffffff');
    var name = escapeHtml(club.name || '');
    var crestSize = 32;
    var fontSize = Math.round(crestSize * 0.32);
    return (
      '<a href="' + escapeHtml(getCatalogHref(club)) + '"' +
      ' class="klubb-header__icon-btn klubb-header__icon-btn--crest"' +      ' aria-label="' + name + '">' +
      '<div class="klubb-crest" role="img" aria-label="' + name + '"' +
      ' style="width:' + crestSize + 'px;height:' + crestSize + 'px;background:' + primary +
      ';color:' + secondary + ';font-size:' + fontSize + 'px;">' +
      crestInnerHtml(club, name, initials) +
      '</div>' +
      '</a>'
    );
  }

  function applyTokens(template, club) {
    var shortName = club.short_name || club.name || '';
    var name = club.name || '';
    return String(template || '')
      .replace(/\[club_short\]/g, shortName)
      .replace(/\[club_name\]/g, name);
  }

  function clubHeroHtml(club) {
    var primary = club.primary_color || '#0a0a0a';
    var secondary = club.secondary_color || '#ffffff';
    var name = escapeHtml(club.name || '');
    var initials = escapeHtml(getInitials(club));
    var shortName = club.short_name || club.name || '';
    var crestSize = 56;
    var fontSize = Math.round(crestSize * 0.32);

    // Defaults can be overridden via data-* attributes on the no-club hero.
    var heroEl = document.querySelector('.klubb-hero');
    var data = (heroEl && heroEl.dataset) || {};
    var eyebrowLabel = data.klubbClubHeroEyebrow || 'Velkommen til';
    var headlineTpl = data.klubbClubHeroHeadline || 'Velkommen til [club_short]-shoppen';
    var subheadTpl = data.klubbClubHeroSubhead || 'Hjemmedrakten, bortedrakten og alt klubbtøy — bestilles og leveres direkte hjem.';
    var primaryCtaTpl = data.klubbClubHeroPrimaryCta || 'Se [club_short]-shoppen';
    var secondaryCtaLabel = data.klubbClubHeroSecondaryCta || 'Bytt klubb';

    var headline = escapeHtml(applyTokens(headlineTpl, club));
    var subhead = escapeHtml(applyTokens(subheadTpl, club));
    var primaryCta = escapeHtml(applyTokens(primaryCtaTpl, club));
    var href = escapeHtml(getCatalogHref(club));
    var patternBg = secondary + '25';
    var patternBgAlt = secondary + '10';

    return (
      '<section class="klubb-club-hero"' +
      ' style="background:' + escapeHtml(primary) + ';color:' + escapeHtml(secondary) + ';">' +
      '<div class="klubb-club-hero__inner klubb-container">' +
      '<div class="klubb-club-hero__copy">' +
      '<div class="klubb-club-hero__crest-row">' +
      '<div class="klubb-crest" role="img" aria-label="' + name + '"' +
      ' style="width:' + crestSize + 'px;height:' + crestSize + 'px;background:' + escapeHtml(primary) +
      ';color:' + escapeHtml(secondary) + ';font-size:' + fontSize + 'px;border:2px solid ' + escapeHtml(secondary) + ';">' +
      crestInnerHtml(club, name, initials) +
      '</div>' +
      '<div>' +
      '<span class="klubb-club-hero__welcome">' + escapeHtml(eyebrowLabel) + '</span>' +
      '<div class="klubb-club-hero__club-name">' + name + '</div>' +
      '</div>' +
      '</div>' +
      '<h1 class="klubb-h-display klubb-club-hero__headline" style="color:' + escapeHtml(secondary) + ';">' +
      headline + '<span class="klubb-dot-accent">.</span>' +
      '</h1>' +
      '<p class="klubb-club-hero__subhead">' + subhead + '</p>' +
      '<div class="klubb-club-hero__ctas">' +
      '<a href="' + href + '" class="klubb-btn klubb-btn--primary">' + primaryCta +
      '<svg class="klubb-icon" viewBox="0 0 24 24" width="14" height="14" aria-hidden="true" focusable="false"><use href="#klubb-icon-arrow"></use></svg>' +
      '</a>' +
      '<button type="button" class="klubb-btn klubb-btn--ghost-light" data-klubb-open-picker>' +
      escapeHtml(secondaryCtaLabel) +
      '</button>' +
      '</div>' +
      '</div>' +
      '<div class="klubb-club-hero__media">' +
      (club.hero_image_url
        ? '<img src="' + escapeHtml(club.hero_image_url) + '" alt="' + name +
          '" class="klubb-club-hero__image" loading="eager">'
        : '<div class="klubb-club-hero__pattern" style="background: repeating-linear-gradient(135deg, ' +
          patternBg + ', ' + patternBg + ' 12px, ' + patternBgAlt + ' 12px, ' + patternBgAlt + ' 24px); color:' + escapeHtml(secondary) + ';">' +
          '<span>' + escapeHtml(shortName) + ' — Drakt 25/26</span>' +
          '</div>'
      ) +
      '</div>' +
      '</div>' +
      '</section>'
    );
  }

  function hydrateHero(club) {
    // Replace the no-club hero with a club-specific hero. Skip if the
    // server already rendered a klubb-club-hero (URL match path).
    if (document.querySelector('.klubb-club-hero')) return;
    var noClubHero = document.querySelector('.klubb-hero');
    if (!noClubHero) return;
    var wrap = document.createElement('div');
    wrap.innerHTML = clubHeroHtml(club);
    noClubHero.replaceWith(wrap.firstChild);
  }

  function hydrate(club, labels) {
    if (!club) return;

    // 1) Sub-nav: replace the "VELG DIN KLUBB" button with the club chip.
    //    Skip if a chip is already present (server-side URL match did it).
    var navInner = document.querySelector('.klubb-header__nav-inner');
    if (navInner && !navInner.querySelector('.klubb-club-chip')) {
      var button = navInner.querySelector('.klubb-nav-item--primary');
      if (button) {
        var wrap = document.createElement('div');
        wrap.innerHTML = chipHtml(club, labels.yourClub, labels.clubHome);
        button.replaceWith(wrap.firstChild);
      }
    }

    // 1b) Ensure the "Bytt klubb" link exists. The server only renders it on
    //     URL-resolved club pages; on client-hydrated pages it's the only way
    //     to reach the picker now that the chip is a home link.
    if (navInner && !navInner.querySelector('.klubb-header__swap-club-link')) {
      var wrapSwap = document.createElement('div');
      wrapSwap.innerHTML = swapLinkHtml(labels.swapClub);
      navInner.appendChild(wrapSwap.firstChild);
    }

    // 2) Icon row: intentionally nothing. The crest icon was removed from
    //    the top row (header cleanup 2026-09-01) — the subnav club chip is
    //    the sole crest affordance now. But DO remove the kundesenter icon
    //    when a club becomes active client-side, matching the server's
    //    no-club-only rendering.
    var iconsRow = document.querySelector('.klubb-header__icons');
    if (iconsRow) {
      var headset = iconsRow.querySelector('.klubb-header__icon-btn--kundesenter');
      if (headset) headset.remove();
    }

    // 3) Hero: swap the no-club hero for a club-themed one on the homepage
    //    (and any other page that rendered the unselected hero).
    hydrateHero(club);

    // 4) PDP no-club banner: server can't read the cookie so it renders the
    //    banner unconditionally on /products/<handle>. Once we know a club
    //    is active client-side, remove the banner so it doesn't reappear
    //    after the .klubb-pending-active-club class is cleared.
    var banner = document.querySelector('.klubb-product__no-club-banner');
    if (banner) banner.remove();

    // 5) Category-PLP hero: same situation as the PDP banner. Swap the
    //    «Velg klubb» pill for the active-club line.
    var catPill = document.querySelector('[data-klubb-cat-hero-noclub]');
    var catLine = document.querySelector('[data-klubb-cat-hero-club]');
    if (catPill && catLine) {
      var catText = catLine.querySelector('[data-klubb-cat-hero-club-text]');
      if (catText) catText.textContent = catText.textContent.replace('%CLUB%', club.name || club.short_name || '');
      catPill.remove();
      catLine.hidden = false;
    }

    // 6) Home: «Slik fungerer det» is a guide for visitors without a club. Hide the
    //    guest-only sections and show the member-only ones instead (D57), filling
    //    [club_name] / [club_short] into the texts that carry data-klubb-tokens.
    //    The template is kept on the element, so running again just redraws it.
    Array.prototype.forEach.call(document.querySelectorAll('[data-klubb-guest-only]'), function (el) {
      el.hidden = true;
    });
    Array.prototype.forEach.call(document.querySelectorAll('[data-klubb-member-only]'), function (el) {
      Array.prototype.forEach.call(el.querySelectorAll('[data-klubb-tokens]'), function (t) {
        if (t.getAttribute('data-klubb-tpl') === null) t.setAttribute('data-klubb-tpl', t.textContent);
        t.textContent = applyTokens(t.getAttribute('data-klubb-tpl'), club);
      });
      el.hidden = false;
    });
  }

  // Money formatting for the club price on cards (the server only formats the
  // regular price). Mirrors Shopify's money_format placeholders.
  function formatMoney(cents, format) {
    var fmt = format || '{{amount}} kr';
    var m = fmt.match(/\{\{\s*(\w+)\s*\}\}/);
    var key = m ? m[1] : 'amount';
    var n = Number(cents) / 100;
    function withDelimiters(value, decimals, thousands, decimal) {
      var fixed = value.toFixed(decimals);
      var parts = fixed.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return parts.length > 1 ? parts[0] + decimal + parts[1] : parts[0];
    }
    var out;
    switch (key) {
      case 'amount_no_decimals': out = withDelimiters(n, 0, ',', '.'); break;
      case 'amount_with_comma_separator': out = withDelimiters(n, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': out = withDelimiters(n, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': out = withDelimiters(n, 2, "'", '.'); break;
      case 'amount_with_space_separator': out = withDelimiters(n, 2, ' ', ','); break;
      case 'amount_no_decimals_with_space_separator': out = withDelimiters(n, 0, ' ', ''); break;
      case 'amount_with_period_and_space_separator': out = withDelimiters(n, 2, ' ', '.'); break;
      default: out = withDelimiters(n, 2, ',', '.');
    }
    return fmt.replace(m ? m[0] : '{{amount}}', out);
  }

  function pageMoneyFormat() {
    var any = document.querySelector('[data-klubb-money-format]');
    return (any && any.getAttribute('data-klubb-money-format')) || '';
  }

  // Cards and strips render the regular price; when the cookie club has a
  // klubbpris and the product is flagged eligible, show the club price here
  // too (the club collection pages already get it from the server).
  // `force` also rewrites prices the server rendered for another club (the
  // URL club of a catalog link the member chose not to switch to).
  function hydratePrices(club, root, force) {
    var pct = club ? Number(club.discount_percent) : 0;
    if (!club || !(pct > 0)) return;
    var label = escapeHtml((club.short_name || club.name || '') + '-pris');
    var scope = root && root.querySelectorAll ? root : document;
    var selector = '[data-klubb-price][data-klubb-discount-eligible="true"]' + (force ? '' : ':not(.klubb-price--club)');
    var nodes = scope.querySelectorAll(selector);
    Array.prototype.forEach.call(nodes, function (el) {
      var cents = Number(el.getAttribute('data-klubb-cents'));
      if (!(cents > 0)) return;
      // D49 — player packages: `cents` is the discountable part, `rest` the part
      // of the package that is never discounted (components without klubbpris).
      // Absent on plain products (= 0).
      var rest = Number(el.getAttribute('data-klubb-cents-rest')) || 0;
      // Older club-rendered spans carry no format of their own: borrow one.
      var format = el.getAttribute('data-klubb-money-format') || pageMoneyFormat();
      var clubCents = Math.round((cents * (100 - pct)) / 100) + rest;
      el.classList.add('klubb-price--club');
      el.innerHTML =
        '<span class="klubb-price__club">' + escapeHtml(formatMoney(clubCents, format)) + '</span>' +
        '<s class="klubb-price__regular">' + escapeHtml(formatMoney(cents + rest, format)) + '</s>' +
        '<span class="klubb-price__label">' + label + '</span>';
    });
  }

  function watchPrices(club) {
    if (typeof MutationObserver === 'undefined') return;
    if (document.body.getAttribute('data-klubb-prices-watched') === 'true') return; // init re-runs on bfcache restores
    document.body.setAttribute('data-klubb-prices-watched', 'true');
    var obs = new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        Array.prototype.forEach.call(m.addedNodes, function (n) {
          // Read the club at mutation time: a catalog link may have switched it.
          if (n.nodeType === 1) hydratePrices((window.klubb && window.klubb.activeClub) || club, n);
        });
      });
    });
    obs.observe(document.body, { childList: true, subtree: true });
  }

  function readLabels() {
    // Liquid passes translated strings via <html data-…> attributes so
    // the JS-rendered chip matches the server-rendered chip per locale.
    var html = document.documentElement;
    return {
      yourClub: html.getAttribute('data-klubb-your-club-label') || 'DIN KLUBB',
      swapClub: html.getAttribute('data-klubb-swap-club-label') || 'BYTT KLUBB',
      clubHome: html.getAttribute('data-klubb-club-home-label') || 'Gå til %CLUB%',
    };
  }

  function clearPendingFlag() {
    document.documentElement.classList.remove('klubb-pending-active-club');
  }

  function wireMenuDrawer() {
    var drawer = document.querySelector('[data-klubb-drawer]');
    if (!drawer) return;
    // Guard against double-wiring (init() re-runs on bfcache restores via
    // pageshow; each run would otherwise re-attach handlers).
    if (drawer.dataset.klubbDrawerWired === 'true') return;
    drawer.dataset.klubbDrawerWired = 'true';
    var openers = document.querySelectorAll('[data-klubb-open-menu]');
    var closers = document.querySelectorAll('[data-klubb-close-menu]');

    function open() {
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      openers.forEach(function (b) { b.setAttribute('aria-expanded', 'true'); });
      document.body.style.overflow = 'hidden';
    }
    function close() {
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      openers.forEach(function (b) { b.setAttribute('aria-expanded', 'false'); });
      document.body.style.overflow = '';
    }

    openers.forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        open();
      });
    });
    closers.forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        close();
      });
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && drawer.classList.contains('is-open')) close();
    });
  }

  // The page says the member kept their own club: the server rendered the
  // chip and the card prices for the URL club, so redraw those for the club
  // that is actually active (the hero stays the catalog's own).
  // «No» to another club's page (19.10.2026): back to the selected club's own
  // catalog, replacing the history entry so Back does not ask again. The
  // target is recorded on <html> first (tests; jsdom cannot navigate).
  function sendBack(club) {
    var href = getCatalogHref(club);
    document.documentElement.setAttribute('data-klubb-sent-back', href);
    try { window.location.replace(href); } catch (e) { /* jsdom */ }
  }

  // Clubs declined in the cart check while no club was selected: not adopted
  // again this session until the member picks that club themselves.
  var DECLINED_KEY = 'klubb_declined_clubs';
  function readDeclined() {
    try {
      var list = JSON.parse(window.sessionStorage.getItem(DECLINED_KEY) || '[]');
      return Array.isArray(list) ? list : [];
    } catch (e) { return []; }
  }
  function writeDeclined(list) {
    try { window.sessionStorage.setItem(DECLINED_KEY, JSON.stringify(list)); } catch (e) { /* private mode */ }
  }
  function isDeclined(slug) { return readDeclined().indexOf(slug) !== -1; }
  function markDeclined(slug) {
    var list = readDeclined();
    if (list.indexOf(slug) === -1) { list.push(slug); writeDeclined(list); }
  }
  function forgetDeclined(slug) {
    var list = readDeclined();
    var i = list.indexOf(slug);
    if (i !== -1) { list.splice(i, 1); writeDeclined(list); }
  }

  // Adopting a club with no club selected (first visit, or after «Fjern valgt
  // klubb» with items still in the cart): run the cart check too, so lines of
  // another club are listed and the rest are re-stamped for this club.
  // Cancel → no club, and the club is not adopted again this session.
  function reconcileAdoption(slug) {
    var reconcile = window.klubbCartReconcile && window.klubbCartReconcile.run;
    if (!reconcile) return;
    reconcile(slug).then(function (res) {
      if (res && res.switched === false) {
        markDeclined(slug);
        window.klubb.clearActiveClub();
      }
    }).catch(function () { /* keep the adoption */ });
  }

  // ?club=<slug> (22.10.2026): a link for a page with no club in its path (home,
  // a general product, a campaign page) names the club to select. Returns the slug;
  // init() looks the club up (exact match on slug among the Active clubs, so a Draft
  // or unknown slug gives no club). The parameter is always taken out of the address
  // bar (other parameters and the hash stay) so shared and bookmarked URLs stay clean.
  function takeClubParam() {
    try {
      var url = new URL(window.location.href);
      if (!url.searchParams.has('club')) return null;
      var slug = url.searchParams.get('club');
      url.searchParams.delete('club');
      window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
      return slug || null;
    } catch (e) { /* no URL/history API: leave the address as it is */ }
    return null;
  }

  // The traede.eligible_for_clubs tokens the product form carries (blank = a general
  // product, eligible for everyone), lower-cased.
  function productTokens() {
    var input = document.querySelector('[data-klubb-eligible-clubs]');
    if (!input) return [];
    return String(input.value || '').toLowerCase().split(/[\s,]+/).filter(Boolean);
  }

  // A club matches a token by slug, short name or crest initials, like the cart reconcile does.
  function clubMatchesTokens(c, tokens) {
    var ids = [c.slug, c.short_name, c.crest_initials].filter(Boolean).map(function (v) { return String(v).toLowerCase(); });
    for (var t = 0; t < tokens.length; t++) {
      if (ids.indexOf(tokens[t]) !== -1) return true;
    }
    return false;
  }

  // The club a product belongs to, from its tokens: null for general products and for clubs
  // that are not published. A product of several clubs counts as the selected club's when
  // it is one of them.
  function productClubOf(clubs, current, tokens) {
    if (!tokens.length) return null;
    if (current && clubMatchesTokens(current, tokens)) return current;
    for (var i = 0; i < clubs.length; i++) {
      if (clubs[i] && clubMatchesTokens(clubs[i], tokens)) return clubs[i];
    }
    return null;
  }

  // A page of another club, its catalog or one of its products, or a ?club=
  // link for another club: ask before switching (19.10.2026). Confirm → cart
  // check → switch; every kind but 'catalog' reloads so price and print follow
  // the new club, a catalog page is already drawn for it. «Bli i …», or cancel
  // in the cart check, keeps the selected club and sends the member back to its
  // catalog ('product', 'catalog') or leaves them where they are ('link': the
  // page is not another club's, so there is nothing to leave). Once per page.
  //   kind: 'product' | 'catalog' | 'link' (also picks the intro text,
  //         data-klubb-pc-tpl-<kind>)
  function offerClubSwitch(target, current, kind) {
    var html = document.documentElement;
    if (html.getAttribute('data-klubb-product-club-offered') === target.slug) return;
    html.setAttribute('data-klubb-product-club-offered', target.slug);
    function commit() {
      window.klubb.setActiveClub(target.slug);
      if (kind !== 'catalog') {
        try { window.location.reload(); } catch (e) { /* jsdom */ }
      }
    }
    function declined() {
      if (kind !== 'link') sendBack(current);
    }
    function switchClub() {
      var reconcile = window.klubbCartReconcile && window.klubbCartReconcile.run;
      if (!reconcile) { commit(); return; }
      reconcile(target.slug).then(function (res) {
        if (res && res.switched === false) { declined(); return; }
        commit();
      }).catch(commit);
    }
    var dialog = document.querySelector('[data-klubb-product-club-dialog]');
    if (!dialog) { switchClub(); return; }
    var clubName = target.name || target.short_name || target.slug;
    var currentName = current.name || current.short_name || current.slug;
    Array.prototype.forEach.call(dialog.querySelectorAll('[data-klubb-pc-tpl]'), function (el) {
      var tpl = (kind !== 'product' && el.getAttribute('data-klubb-pc-tpl-' + kind)) || el.getAttribute('data-klubb-pc-tpl');
      el.textContent = String(tpl || '').replace(/%CLUB%/g, clubName).replace(/%CURRENT%/g, currentName);
    });
    var confirmBtn = dialog.querySelector('[data-klubb-product-club-confirm]');
    var cancelBtn = dialog.querySelector('[data-klubb-product-club-cancel]');
    function close() {
      dialog.hidden = true;
      document.documentElement.style.overflow = '';
      if (confirmBtn) confirmBtn.removeEventListener('click', onConfirm);
      if (cancelBtn) cancelBtn.removeEventListener('click', onCancel);
    }
    function onConfirm() { close(); switchClub(); }
    function onCancel() { close(); declined(); }
    if (confirmBtn) confirmBtn.addEventListener('click', onConfirm);
    if (cancelBtn) cancelBtn.addEventListener('click', onCancel);
    dialog.hidden = false;
    document.documentElement.style.overflow = 'hidden';
    if (confirmBtn && confirmBtn.focus) confirmBtn.focus();
  }

  function init() {
    // The menu drawer is independent of active-club state — wire it
    // unconditionally before the club-hydration logic.
    wireMenuDrawer();

    if (!window.klubb) return;
    var k = window.klubb;
    // A page carries only its own club (R2, D70). The member's club comes first: from the stored copy at once,
    // else looked up (ready() gives up after 4 s and the header then shows the no-club state).
    Promise.resolve(typeof k.ready === 'function' ? k.ready() : null).catch(function () { return null; }).then(function () {
      var club = k.activeClub;
      var urlSlug = document.documentElement.getAttribute('data-klubb-url-club');
      // ?club=<slug> link. Always read, so the parameter leaves the address bar,
      // but a club in the path wins over it.
      var linkSlug = takeClubParam();
      if (urlSlug) linkSlug = null;
      // Only plain /products/ pages: under a catalog URL the URL club wins.
      var tokens = urlSlug ? [] : productTokens();
      // Other clubs are looked up only when a decision needs them: a ?club= link, or a club product that
      // is not the member's own club.
      var needDirectory = tokens.length > 0 && !(club && clubMatchesTokens(club, tokens));
      return Promise.all([
        linkSlug ? k.whenClub(linkSlug) : null,
        needDirectory ? k.directory() : null,
      ]).then(function (found) { start(k, club, urlSlug, tokens, found[0], found[1]); });
    }).catch(function () { clearPendingFlag(); });
  }

  function start(k, club, urlSlug, tokens, linkClub, directoryList) {
    var productClub = productClubOf(directoryList || k.knownClubs(), club, tokens);
    // Picking a club yourself (it is active now) undoes an earlier «no».
    if (club) forgetDeclined(club.slug);
    // So does following a link for the club: it is asked again.
    if (linkClub) forgetDeclined(linkClub.slug);
    if (!club) {
      // Direct-link adoption: a member may open their club's catalog URL
      // (e.g. /collections/<club>-katalog) directly. The server resolves the
      // club from the URL and exposes its slug below; if no cookie is set
      // yet, adopt it so the selection persists across pages — no picker.
      // A club-specific product opened without a club adopts the same way, and
      // so does a ?club=<slug> link (it wins over the product's club).
      // The cart check runs on whatever is already in the cart.
      var adoptSlug = urlSlug || (linkClub && linkClub.slug) || (productClub && productClub.slug);
      if (adoptSlug && isDeclined(adoptSlug)) adoptSlug = null;
      if (adoptSlug && window.klubb.setActiveClub) {
        window.klubb.setActiveClub(adoptSlug);
        club = window.klubb.activeClub;
        if (club) whenScriptsReady(function () { reconcileAdoption(adoptSlug); });
      }
    } else if (urlSlug && urlSlug !== club.slug) {
      // A link to another club's catalog: ask, switch or go back.
      var urlClub = k.clubBySlug(urlSlug); // the page carries its own club
      var fromClub = club;
      whenScriptsReady(function () { offerClubSwitch(urlClub || { slug: urlSlug }, fromClub, 'catalog'); });
    } else if (linkClub && linkClub.slug !== club.slug) {
      // A ?club= link for another club: the same question; «Bli i …» stays on this page.
      var linkFrom = club;
      whenScriptsReady(function () { offerClubSwitch(linkClub, linkFrom, 'link'); });
    } else if (productClub && productClub.slug !== club.slug) {
      // Another club's product: the same question.
      var ownClub = club;
      whenScriptsReady(function () { offerClubSwitch(productClub, ownClub, 'product'); });
    }
    if (!club) {
      // Cookie was stale and got cleared by klubb-club-state.js — drop the
      // pre-paint placeholder marker so the unselected UI becomes visible.
      clearPendingFlag();
      return;
    }
    hydrate(club, readLabels());
    hydratePrices(club);
    watchPrices(club);
    clearPendingFlag();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Back/forward navigations restore the page from the bfcache WITHOUT firing
  // DOMContentLoaded, so a returning club visitor could land on the un-hydrated
  // (generic) homepage hero on those loads. Re-run the idempotent hydration on
  // bfcache restores so the active-club hero stays consistent across all loads.
  window.addEventListener('pageshow', function (e) {
    if (e.persisted) init();
  });
})();
